/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { AnalyticsRecorder, type AnalyticsOptions, type ReportingEvents } from '../analytics';
import { FrequencyStore, type FrequencyContext, type PageSignals } from '../activity/frequency';
import { Opportunity } from '../auction';
import { DataCollector } from '../collection/index';
import { EventDispatcher } from '../events';
import { Logger } from '../logger';
import { AdCommunicator, NodalsMessageEvent, type AdCommunicatorEvents } from '../messaging';
import type { Facts } from '../rules';
import { GlobalFactStore } from '../rules/facts/store';
import { generateUUID, getPrebidUserId, hashObject } from '../util';
import { AdvertorialTracker } from './advertorial';
import { SiteConfig } from './config';
import { PageEngagement, getPageDwellTimeSeconds } from './engagement';
import { Placement } from './placement';
import { PlacementManager } from './placement/manager';
import { getTransformerFromConfig } from './placement/transformers';

import {
  AdAssignedKeyValues,
  CampaignType,
  SelectionReportingPlacementData,
  type CampaignData,
  type KeyValue,
  type PlacementAssignedKeyValues,
  type SelectionEndpointPlacementData,
} from '../types';
import type {
  PrebidAdUnit,
  PrebidFormattedKeyValues,
  RtdModuleConfig,
  UserConsent,
} from '../types/prebid';

const logger = Logger.getInstance();

interface SessionData {
  id: string;
  createdAt: number; // epoch ms
  /** Pages visited this session. Optional: legacy stored values lack it. */
  pageDepth?: number;
}

class PageSession {
  private static readonly STORAGE_KEY = 'nodals.session';
  private static readonly SESSION_TTL_MS = 24 * 60 * 60 * 1000;

  private cached: SessionData | null = null;

  public getId(): string {
    if (this.cached && !this.isExpired(this.cached)) {
      return this.cached.id;
    }

    try {
      const existingSession = sessionStorage.getItem(PageSession.STORAGE_KEY);
      if (existingSession) {
        const sessionData: SessionData = JSON.parse(existingSession);
        if (sessionData.id && !this.isExpired(sessionData)) {
          this.cached = sessionData;
          return sessionData.id;
        }
      }
    } catch (error) {
      logger.warn(`Session data retrieval failed: ${error} - creating new session`);
    }

    return this.createNewSession();
  }

  /**
   * Count this page view against the session. Resolves the current session
   * (minting one when absent/expired), increments its page depth and
   * best-effort persists — a storage failure only loses the persisted count,
   * never the in-memory one.
   */
  public incrementPageDepth(): void {
    // getId() resolves-or-mints the session and populates the cache.
    this.getId();
    if (!this.cached) {
      return;
    }
    this.cached.pageDepth = (this.cached.pageDepth ?? 0) + 1;
    try {
      sessionStorage.setItem(PageSession.STORAGE_KEY, JSON.stringify(this.cached));
    } catch (error) {
      logger.warn(`Session data storage failure: ${error}`);
    }
  }

  /** Pages visited this browsing session (0 when never incremented). */
  public getPageDepth(): number {
    this.getId();
    return this.cached?.pageDepth ?? 0;
  }

  private isExpired(sessionData: SessionData): boolean {
    // Sessions without a numeric createdAt (legacy values) have unknown age
    return (
      typeof sessionData.createdAt !== 'number' ||
      !(Date.now() - sessionData.createdAt < PageSession.SESSION_TTL_MS)
    );
  }

  private createNewSession(): string {
    const sessionId = generateUUID();
    const sessionData: SessionData = { id: sessionId, createdAt: Date.now() };
    this.cached = sessionData;

    try {
      sessionStorage.setItem(PageSession.STORAGE_KEY, JSON.stringify(sessionData));
    } catch (error) {
      // SessionStorage might not be available (e.g., private browsing)
      logger.warn(`Session data storage failure: ${error}`);
    }

    return sessionId;
  }
}

class Page extends EventDispatcher<ReportingEvents> {
  private adCommunicator: AdCommunicator = AdCommunicator.getInstance();
  protected placementMap: Map<string, Placement> = new Map<string, Placement>();
  private _userConsent: UserConsent | null = null;
  protected placementManager: PlacementManager;
  protected siteConfig: SiteConfig;
  protected config: RtdModuleConfig | null = null;
  private analyticsRecorder: AnalyticsRecorder | null = null;
  private advertorialTracker: AdvertorialTracker | null = null;
  private session: PageSession = new PageSession();
  private pageViewId: string;
  private pageViewRecorded: boolean = false;
  private ppuid: string | undefined = undefined;
  private campaignDataHash: number = 0;
  private auctionCount: number = 0;
  private auctionOpportunityMap: Map<string, Opportunity> = new Map();
  private defaultSelectionLabel: string | undefined = undefined;

  protected constructor(siteConfig: SiteConfig) {
    super();
    this.siteConfig = siteConfig;
    this.pageViewId = generateUUID();
    this.placementManager = PlacementManager.getInstance(
      (
        adUnit: PrebidAdUnit,
        adUnitTransformer: (adUnitCode: string) => string,
        campaignData: CampaignData[],
        siteConfig: SiteConfig,
      ) => {
        return this.createPlacement(adUnit, adUnitTransformer, campaignData, siteConfig);
      },
    );
    // Count this page view against the browsing session and start the
    // one-shot page-engagement listeners.
    this.session.incrementPageDepth();
    PageEngagement.getInstance().init();
    // Opportunities minted outside an auction (auction-less refresh renders,
    // video follow-on ads) never pass through initialiseAuction's snapshot;
    // they capture page signals at render time through this provider.
    FrequencyStore.getInstance().registerPageSignalsProvider(() => this.getPageSignals());
    this.addListeners();
  }

  public init(): void {
    const adServerTargeting = this.extractAdServerTargeting();
    if (Object.keys(adServerTargeting).length > 0) {
      GlobalFactStore.getInstance().setAdServerPageTargeting(adServerTargeting);
      logger.debug(
        `Page level ad server targeting extracted: ${JSON.stringify(adServerTargeting)}`,
      );
    }
  }

  private static instance: Page;

  public static getInstance(siteConfig: SiteConfig): Page {
    if (!Page.instance) {
      Page.instance = new Page(siteConfig);
    }
    return Page.instance;
  }

  get isVisible(): boolean {
    return document.visibilityState === 'visible' || document.hidden === false;
  }

  // Consent is guaranteed set by PageFactory.create / engine.getPage before any dispatch.
  private get userConsent(): UserConsent {
    return this._userConsent!;
  }

  /**
   * Normalizes ad server targeting data with 'page.kvs.' prefix
   * Preserves array values for rule matching, converts primitives to strings
   */
  protected normalizeAdServerTargeting(targeting: Record<string, any>): Facts {
    const normalized: Facts = {};
    for (const [key, value] of Object.entries(targeting)) {
      const prefixedKey = `page.kvs.${key}`;
      if (Array.isArray(value)) {
        // Preserve arrays for rule operators like 'anyin' and 'allin'
        // Convert primitive elements to strings
        normalized[prefixedKey] = value.map((v) => String(v));
      } else if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        normalized[prefixedKey] = String(value);
      }
      // Ignore objects and other complex types
    }
    return normalized;
  }

  protected extractAdServerTargeting(): Facts {
    return {};
  }

  public setUserConsent(userConsent: UserConsent): void {
    this._userConsent = userConsent;
  }

  public initialiseAuction(
    adUnits: PrebidAdUnit[],
    campaignData: CampaignData[],
    auctionId?: string,
  ): Opportunity {
    this.auctionCount++;
    this.defaultSelectionLabel = undefined;
    const opportunity = new Opportunity(generateUUID());

    // One page-signals snapshot per opportunity, taken at auction time and
    // shared by the select payload and every resulting impression (learning,
    // monitoring and scoring impressions included).
    FrequencyStore.getInstance().setPageSignals(opportunity.id, this.getPageSignals());

    if (this.hasExceededAuctionLimit()) {
      logger.info(
        `Auction limit exceeded (${this.auctionCount}/${this.siteConfig.maxRecordedAuctions}), skipping auction processing`,
      );
      return opportunity;
    }

    if (auctionId) {
      this.auctionOpportunityMap.set(auctionId, opportunity);
    }

    const placements = this.getOrCreatePlacements(adUnits, campaignData);

    if (this.auctionCount === 1 && !this.advertorialTracker) {
      if (this.shouldStartAdvertorialTracking(campaignData)) {
        this.startAdvertorialTracking(campaignData);
      } else {
        logger.info(
          'No LEARNING_DIRECT campaign or MONITORING campaign with eurl found, advertorial tracking disabled',
        );
      }
    }

    // Check if there are any eligible campaigns before dispatching auction start
    if (this.hasAnyEligibleCampaigns()) {
      // Record the page view here, not solely on consent: placements are created above, so this is
      // the first point where eligibility is genuinely known. recordPageView() is latched and
      // eligibility-gated, so it dispatches at most once and is safe to call on every auction.
      this.recordPageView();

      this.dispatch('AuctionStart', {
        opportunityId: opportunity.id,
        placements: placements,
        userConsent: this.userConsent,
      });
    } else {
      logger.info('No eligible campaigns found, skipping auction start recording.');
    }

    placements.forEach((placement) => {
      try {
        placement.newOpportunity(opportunity);
      } catch (error) {
        logger.error(`Failed to create opportunity for placement: ${error}`);
      }
    });

    logger.debug(
      `Auction initialised with ${placements.length} placements and opportunity ${opportunity.id}`,
    );
    return opportunity;
  }

  public endAuction(adUnitCodes: string[], auctionId?: string): void {
    // Opportunity recording was retired server-side, so auction end only
    // releases the auction→opportunity join entry.
    if (auctionId) {
      this.auctionOpportunityMap.delete(auctionId);
    }
    logger.debug(`Auction ended for adUnits: ${adUnitCodes.join(', ')}`);
  }

  public enableAnalytics(analyticsOptions: AnalyticsOptions): void {
    if (!this.analyticsRecorder) {
      this.analyticsRecorder = new AnalyticsRecorder(this as any, analyticsOptions);
      logger.debug('Analytics recorder enabled for Page');
    }
  }

  private shouldStartAdvertorialTracking(campaignData: CampaignData[]): boolean {
    return campaignData.some(
      (campaign) =>
        (campaign.type === CampaignType.LEARNING_DIRECT && campaign.ads.length > 0) ||
        (campaign.type === CampaignType.MONITORING && campaign.ads.some((ad) => ad.eurl)),
    );
  }

  private startAdvertorialTracking(campaignData: CampaignData[]): void {
    // Check if already initialized
    if (this.advertorialTracker) {
      return;
    }

    // Check all required conditions
    const config = this.siteConfig.advertorialConfig;
    if (!config) {
      return; // No config in siteConfig
    }

    if (!this._userConsent) {
      return; // No user consent yet
    }

    // All conditions met - create tracker
    logger.info('Starting advertorial tracking');
    this.advertorialTracker = new AdvertorialTracker(config, campaignData);
    this.advertorialTracker.on('AdvertorialImpression', ({ data }) => {
      this.dispatch('AdvertorialImpression', {
        ...this.getEventContextData(data.opportunityId, data.placementId, {
          id: data.id,
          deliveryId: data.deliveryId,
          meta: data.meta,
          t: data.t,
          v: data.v,
          pt: data.pt,
          xd: this.getPageSignals(),
        }),
      });
    });
    this.advertorialTracker.on('AdvertorialInteraction', ({ data }) => {
      this.dispatch('AdInteraction', {
        ...this.getEventContextData(data.opportunityId, data.placementId, {
          impressionId: data.id,
          deliveryId: data.deliveryId,
          useHttpPost: true,
          t: data.t,
        }),
      });
    });
    this.advertorialTracker.on('AdvertorialViewableImpression', ({ data }) => {
      this.dispatch('AdViewableImpression', {
        ...this.getEventContextData(data.opportunityId, data.placementId, {
          impressionId: data.impressionId,
          deliveryId: data.deliveryId,
          t: data.t,
        }),
      });
    });
    this.advertorialTracker.on('AdvertorialAccidentalClick', ({ data }) => {
      this.dispatch('AccidentalClick', {
        ...this.getEventContextData(data.opportunityId, data.placementId, {
          impressionId: data.id,
          deliveryId: data.deliveryId,
          awayDuration: data.awayDuration,
          source: data.source,
          t: data.t,
        }),
      });
    });
    this.advertorialTracker.start();
  }

  public setConfig(config: RtdModuleConfig): void {
    this.config = config;
    const eidSource = this.config?.params.userIdEidSource || this.siteConfig.userIdEidSource;
    if (eidSource) {
      this.ppuid = getPrebidUserId(eidSource) ?? undefined;
    }
  }

  public setPlacementKeyValues(
    adUnitCodes: string[],
    placementKeyValues: PlacementAssignedKeyValues[],
    selectionLabel?: string,
  ): void {
    this.validatePlacements();
    this.defaultSelectionLabel = selectionLabel;
    adUnitCodes.forEach((adUnitCode) => {
      const placement = this.placementMap.get(adUnitCode);
      if (placement) {
        const keyValuesForAdUnit = placementKeyValues.find((kv) => kv.gad === placement.adUnitCode);
        if (keyValuesForAdUnit) {
          placement.setKeyValues(keyValuesForAdUnit);
        }
      } else {
        logger.warn(`No placement found for adUnit: ${adUnitCode}`);
      }
    });
  }

  public getKeyValuesForAdUnits(
    adUnitArray: string[],
    campaignTypes: CampaignType[],
  ): PrebidFormattedKeyValues {
    // Record the page view here too: getTargetingData can run before getBidRequestData (the library
    // may load mid-cycle and miss the auction start). recordPageView is latched and guarded, so the
    // page view fires from whichever lifecycle method observes placements + eligibility + consent
    // first. Placed before the visibility gate for parity with initialiseAuction.
    this.recordPageView();

    const targetingData: PrebidFormattedKeyValues = {};
    if (!this.isVisible) {
      logger.info('Page is not visible, skipping key value retrieval');
      return targetingData;
    }

    // Create a map to track which adUnits are served by which placements
    const placementToAdUnitCodes = new Map<Placement, string[]>();

    adUnitArray.forEach((adUnitCode) => {
      const placement = this.placementMap.get(adUnitCode);
      if (placement) {
        if (!placementToAdUnitCodes.has(placement)) {
          placementToAdUnitCodes.set(placement, []);
        }
        placementToAdUnitCodes.get(placement)!.push(adUnitCode);
      }
    });

    let hasRelevantCampaigns = false;

    placementToAdUnitCodes.forEach((adUnitCodes, placement) => {
      const keyValues: KeyValue[] = placement.getKeyValuesForCampaignType(campaignTypes);
      if (keyValues.length > 0) {
        // Apply same key values to all adUnits served by this placement (handles aliasing)
        adUnitCodes.forEach((adUnitCode) => {
          targetingData[adUnitCode] = {};
          keyValues.forEach((kv) => {
            targetingData[adUnitCode][kv.key] = this.hasExceededAuctionLimit() ? '' : kv.value;
          });
        });
      }

      if (placement.hasAnyCampaignForTypes(campaignTypes)) {
        hasRelevantCampaigns = true;
      }
    });

    if (hasRelevantCampaigns) {
      this.dispatchTargetingSelectionComplete(adUnitArray, campaignTypes);
    }

    return targetingData;
  }

  public getFilteredKeyValuesByPlacementByAd(
    adUnitArray: string[],
    campaignTypes?: CampaignType[],
  ): SelectionReportingPlacementData[] {
    const reportingData: SelectionReportingPlacementData[] = [];
    const uniquePlacements = this.getUniquePlacements(adUnitArray);

    uniquePlacements.forEach((placement) => {
      let adAssignedKeyValues: AdAssignedKeyValues[] = [];

      // Filter by campaign types if provided
      if (campaignTypes && campaignTypes.length > 0) {
        adAssignedKeyValues = placement.getAdKeyValuesByAdForCampaignTypes(campaignTypes);
      } else {
        adAssignedKeyValues = placement.getAdKeyValuesByAd();
      }

      // Filter out AdAssignedKeyValues with empty kvs arrays
      const filteredAdAssignedKeyValues = adAssignedKeyValues.filter(
        (ad) => ad.kvs && ad.kvs.length > 0,
      );

      if (filteredAdAssignedKeyValues.length > 0) {
        reportingData.push({
          gad: placement.adUnitCode,
          ads: filteredAdAssignedKeyValues,
        });
      }
    });

    return reportingData;
  }

  public getEligibleCampaignsByAdUnit(
    adUnitCodes: string[],
    campaignTypes: CampaignType[],
  ): SelectionEndpointPlacementData[] {
    if (this.hasExceededAuctionLimit()) {
      return [];
    }

    const placementEligibleCampaigns: SelectionEndpointPlacementData[] = [];
    const uniquePlacements = this.getUniquePlacements(adUnitCodes);

    // One reference moment per auction: every candidate's exposure windows
    // are computed against the same `now`, so the whole payload shares a
    // single point.
    const freqCtx: FrequencyContext = { now: Date.now() };

    uniquePlacements.forEach((placement) => {
      placementEligibleCampaigns.push({
        gad: placement.adUnitCode,
        ss: placement.supportedSizes
          .filter((size) => size != null)
          .map((size) => ({
            w: size.width,
            h: size.height,
          })),
        v: placement.visibility,
        t: placement.placementType,
        campaigns: placement.getEligibleAdIdsByCampaign(campaignTypes, freqCtx),
      });
    });

    return placementEligibleCampaigns;
  }

  protected getSessionId(): string {
    return this.session.getId();
  }

  /**
   * Page signals: scroll position (psd), session page depth (spd), the
   * one-shot engagement flag (pen) and page dwell time (pdt). For auctions,
   * captured per opportunity in initialiseAuction and locked from then on —
   * the select payload and every resulting impression (learning/monitoring/
   * scoring included) replay these exact values. Advertorial impressions have
   * no auction to freeze against and read live values at dispatch instead.
   */
  public getPageSignals(): PageSignals {
    let scrollY = 0;
    try {
      scrollY = Math.max(0, Math.round(window.scrollY ?? 0));
    } catch (error) {
      // Non-browser environment; report zero.
    }
    return {
      psd: scrollY,
      spd: this.session.getPageDepth(),
      pen: PageEngagement.getInstance().hasEngaged() ? 1 : 0,
      pdt: getPageDwellTimeSeconds(),
    };
  }

  private hasExceededAuctionLimit(): boolean {
    return (
      Boolean(this.siteConfig.maxRecordedAuctions) &&
      this.auctionCount > this.siteConfig.maxRecordedAuctions
    );
  }

  private hasAnyEligibleCampaigns(): boolean {
    const allCampaignTypes = [
      CampaignType.DIRECT,
      CampaignType.DEAL,
      CampaignType.LEARNING_DIRECT,
      CampaignType.LEARNING_PROGRAMMATIC,
      CampaignType.SCORING_DIRECT,
      CampaignType.SCORING_DEAL,
      CampaignType.MONITORING,
    ];

    const allAdUnitCodes = Array.from(this.placementMap.keys());
    if (allAdUnitCodes.length === 0) return false;

    // Deliberately NOT getEligibleCampaignsByAdUnit: this gate spans ALL
    // campaign types, and the payload builder would compute and register
    // frequency snapshots for observational campaigns — leaking xd onto t=1
    // impressions and churning the snapshot store with gate-only entries.
    return this.getUniquePlacements(allAdUnitCodes).some((placement) =>
      placement.hasEligibleCampaigns(allCampaignTypes),
    );
  }

  protected createPlacement(
    // @ts-ignore
    adUnit: PrebidAdUnit,
    // @ts-ignore
    adUnitTransformer: (adUnitCode: string) => string,
    // @ts-ignore
    campaignData: CampaignData[],
    // @ts-ignore
    siteConfig: SiteConfig,
  ): Placement | undefined {
    throw new Error('Generic page cannot create non-video placements directly');
  }

  protected locateContainingPlacement(event: {
    data: AdCommunicatorEvents['TaggedAdServed'] | AdCommunicatorEvents['AdInteraction'];
  }): Placement | null {
    const targetWindow = event.data.window;

    // First try: window-based lookup using iframe matching
    const iframes = document.querySelectorAll('iframe');

    for (const iframe of iframes) {
      if (iframe.contentWindow === targetWindow) {
        for (const [, placement] of this.placementMap) {
          if (placement.domElement?.contains(iframe)) {
            logger.info(
              `TaggedAdServed event iframe match found for adUnit: ${placement.adUnitCode}`,
            );
            return placement;
          }
        }
      }
    }

    return null;
  }

  protected getUniquePlacements(adUnitCodes: string[]): Placement[] {
    this.validatePlacements();
    const uniquePlacements = new Set<Placement>();

    adUnitCodes.forEach((adUnitCode) => {
      const placement = this.placementMap.get(adUnitCode);
      if (placement) {
        uniquePlacements.add(placement);
      }
    });

    return Array.from(uniquePlacements);
  }

  private getEventContextData<T extends Record<string, any>>(
    opportunityId: string,
    adUnitCode: string,
    additionalData: T = {} as T,
  ): {
    opportunityId: string;
    adUnit: string;
    pvid: string;
    sid: string;
    uid?: string;
    userConsent: UserConsent;
  } & T {
    // Only set selectionLabel for server-selection eligible campaigns (t !== 1)
    // t: 1 = observational campaigns (LEARNING_DIRECT, LEARNING_PROGRAMMATIC, MONITORING)
    // t: 0 = server-selection campaigns (DIRECT, DEAL, SCORING_DIRECT, SCORING_DEAL)
    const isObservationalEvent = (additionalData as any).t === 1;
    const adSelectionLabel = (additionalData as any).selectionLabel;
    const selectionLabel = isObservationalEvent
      ? undefined
      : (adSelectionLabel ?? this.defaultSelectionLabel);

    // Destructure selectionLabel out so it doesn't override the computed value
    const { selectionLabel: _adLabel, ...restAdditionalData } = additionalData as any;

    return {
      opportunityId,
      adUnit: adUnitCode,
      pvid: this.pageViewId,
      sid: this.getSessionId(),
      uid: this.ppuid,
      selectionLabel,
      userConsent: this.userConsent,
      ...restAdditionalData,
    };
  }

  private setupPlacementEventListeners(placement: Placement): void {
    placement.on('DirectServedAdImpression', (event) => {
      // Honour the opportunity id the placement stamped on the event (a video
      // placement mints a fresh id per ad for pods/auto-next); fall back to the
      // placement's current opportunity for events that don't carry one.
      const opportunityId = event.data.opportunityId ?? placement.currentOpportunity?.id;
      if (opportunityId && !this.hasExceededAuctionLimit()) {
        this.dispatch('AdImpression', {
          ...this.getEventContextData(opportunityId, placement.adUnitCode, {
            deliveryId: event.data.deliveryId,
            id: event.data.id,
            advertiserId: event.data.advertiserId,
            lineItemId: event.data.lineItemId,
            kvs: event.data.kvs,
            creativeId: event.data.creativeId,
            advertiserDomains: event.data.advertiserDomains,
            renderedData: { size: event.data.size },
            durationSeconds: event.data.durationSeconds,
            selectionLabel: event.data.selectionLabel,
            t: event.data.t,
            v: placement.visibility,
            pt: placement.placementType,
            xd: event.data.xd,
          }),
        });
      }
    });

    placement.on('ProgrammaticServedAdImpression', (event) => {
      const opportunityId = event.data.opportunityId ?? placement.currentOpportunity?.id;
      if (opportunityId && !this.hasExceededAuctionLimit()) {
        this.dispatch('AdImpression', {
          ...this.getEventContextData(opportunityId, placement.adUnitCode, {
            deliveryId: event.data.deliveryId,
            id: event.data.id,
            advertiserDomains: event.data.advertiserDomains,
            renderedData: { size: event.data.size },
            durationSeconds: event.data.durationSeconds,
            nodalsDealId: event.data.nodalsDealId,
            t: event.data.t,
            v: placement.visibility,
            pt: placement.placementType,
            xd: event.data.xd,
          }),
        });
      }
    });

    placement.on('AdInteraction', (event) => {
      const opportunityId = event.data.opportunityId ?? placement.currentOpportunity?.id;
      if (opportunityId && !this.hasExceededAuctionLimit()) {
        this.dispatch('AdInteraction', {
          ...this.getEventContextData(opportunityId, placement.adUnitCode, {
            deliveryId: event.data.deliveryId,
            impressionId: event.data.impressionId,
            advertiserId: event.data.advertiserId,
            advertiserDomains: event.data.advertiserDomains,
            lineItemId: event.data.lineItemId,
            nodalsDealId: event.data.nodalsDealId,
            useHttpPost: event.data.useHttpPost,
            selectionLabel: event.data.selectionLabel,
            t: event.data.t,
          }),
        });
      }
    });

    placement.on('AccidentalClick', (event) => {
      const opportunityId = event.data.opportunityId ?? placement.currentOpportunity?.id;
      if (opportunityId && !this.hasExceededAuctionLimit()) {
        this.dispatch('AccidentalClick', {
          ...this.getEventContextData(opportunityId, placement.adUnitCode, {
            deliveryId: event.data.deliveryId,
            impressionId: event.data.impressionId,
            advertiserId: event.data.advertiserId,
            advertiserDomains: event.data.advertiserDomains,
            lineItemId: event.data.lineItemId,
            nodalsDealId: event.data.nodalsDealId,
            selectionLabel: event.data.selectionLabel,
            awayDuration: event.data.awayDuration,
            source: event.data.source,
            t: event.data.t,
          }),
        });
      }
    });

    placement.on('ViewableImpression', (event) => {
      const opportunityId = event.data.opportunityId ?? placement.currentOpportunity?.id;
      if (opportunityId && !this.hasExceededAuctionLimit()) {
        this.dispatch('AdViewableImpression', {
          ...this.getEventContextData(opportunityId, placement.adUnitCode, {
            deliveryId: event.data.deliveryId,
            impressionId: event.data.impressionId,
            advertiserId: event.data.advertiserId,
            advertiserDomains: event.data.advertiserDomains,
            lineItemId: event.data.lineItemId,
            nodalsDealId: event.data.nodalsDealId,
            useHttpPost: event.data.useHttpPost,
            selectionLabel: event.data.selectionLabel,
            t: event.data.t,
          }),
        });
      }
    });

    placement.on('VideoAdPlaybackQuartileReached', (event) => {
      logger.info(
        `Placement dispatching AdPlaybackQuartileReached quartile: ${event.data.quartile}`,
      );
      const opportunityId = event.data.opportunityId ?? placement.currentOpportunity?.id;
      if (opportunityId && !this.hasExceededAuctionLimit()) {
        this.dispatch('VideoQuartile', {
          ...this.getEventContextData(opportunityId, placement.adUnitCode, {
            deliveryId: event.data.deliveryId,
            impressionId: event.data.impressionId,
            quartile: event.data.quartile,
            advertiserId: event.data.advertiserId,
            advertiserDomains: event.data.advertiserDomains,
            lineItemId: event.data.lineItemId,
            nodalsDealId: event.data.nodalsDealId,
            useHttpPost: event.data.useHttpPost,
            selectionLabel: event.data.selectionLabel,
            t: event.data.t,
          }),
        });
      }
    });

    placement.on('BrandSafetyViolation', (event) => {
      const currentOpportunity = placement.currentOpportunity;
      if (currentOpportunity && !this.hasExceededAuctionLimit()) {
        this.dispatch('BrandSafetyViolation', {
          cid: event.data.campaignId,
          pvid: this.pageViewId,
          sid: this.getSessionId(),
          uid: this.ppuid,
          adUnit: placement.adUnitCode,
          detectedWords: event.data.detectedWords,
          userConsent: this.userConsent,
          bidStage: event.data.bidStage,
          opportunityId: currentOpportunity.id,
        });
      }
    });
  }

  private getOrCreatePlacements(
    adUnits: PrebidAdUnit[],
    campaignData: CampaignData[],
  ): Placement[] {
    const placements: Placement[] = [];
    const currentHash = hashObject(campaignData);
    const campaignDataChanged = currentHash !== this.campaignDataHash;

    adUnits.forEach((adUnit) => {
      let placement = this.placementMap.get(adUnit.code);

      if (!placement) {
        try {
          const adUnitTransformer = getTransformerFromConfig(
            this.siteConfig,
            this.config?.params?.adUnitTransformer,
          );
          placement = this.placementManager.getPlacement(
            adUnit,
            campaignData,
            adUnitTransformer,
            this.siteConfig,
          );
          if (placement) {
            const isAliasedPlacement = [...this.placementMap.values()].includes(placement);
            if (!isAliasedPlacement) {
              this.setupPlacementEventListeners(placement);
              if (!placement.init()) {
                logger.warn(
                  `Slot detection failed on Placement initialisation for adUnit: ${adUnit.code}`,
                );
              }
            }
            this.placementMap.set(adUnit.code, placement);
          } else {
            logger.error(`Failed to initialise placement for adUnit: ${adUnit.code}`);
            return;
          }
        } catch (error) {
          logger.error(`Failed to create placement for adUnit ${adUnit.code}: ${error}`);
          return;
        }
      } else if (campaignDataChanged) {
        // Only update campaigns if campaign data has actually changed
        placement.updateCampaigns(campaignData);
      }

      placements.push(placement);
    });

    // Update stored hash if campaign data changed
    if (campaignDataChanged) {
      this.campaignDataHash = currentHash;
    }

    return [...new Set(placements)]; // Return unique placements
  }

  private recordPageView(): void {
    if (this.pageViewRecorded) {
      logger.warn('Page view has already been recorded.');
      return;
    }

    // Never record a page view without real consent; defer until consent is present.
    if (!this._userConsent) {
      logger.info('No user consent yet, deferring page view recording.');
      return;
    }

    // Check if there are any eligible campaigns before recording page view
    if (!this.hasAnyEligibleCampaigns()) {
      logger.info('No eligible campaigns found, skipping page view recording.');
      return;
    }

    const { xd, mainUserId, pcids } = DataCollector.getInstance().collect();
    this.dispatch('PageView', {
      id: this.pageViewId,
      sid: this.getSessionId(),
      // A dcfg-designated main user id supersedes the legacy eidsrc-derived uid.
      uid: mainUserId ?? this.ppuid,
      userConsent: this._userConsent,
      ...(Object.keys(xd).length > 0 && { xd }),
      ...(pcids && pcids.length > 0 && { pcids }),
    });

    this.pageViewRecorded = true;
  }

  private dispatchTargetingSelectionComplete(
    adUnitArray: string[],
    campaignTypes: CampaignType[],
  ): void {
    if (this.hasExceededAuctionLimit()) {
      return;
    }

    // Get current opportunity ID from placements
    let opportunityId: string | null = null;
    const uniquePlacements = this.getUniquePlacements(adUnitArray);

    for (const placement of uniquePlacements) {
      if (placement.currentOpportunity && !opportunityId) {
        opportunityId = placement.currentOpportunity.id;
        break;
      }
    }

    // Only dispatch if we have an opportunity ID and user consent
    if (opportunityId && this._userConsent) {
      const selectionData = this.getFilteredKeyValuesByPlacementByAd(adUnitArray, campaignTypes);

      if (selectionData.length > 0) {
        this.dispatch('TargetingSelectionComplete', {
          opportunityId,
          selectionData,
          userConsent: this._userConsent,
          selectionLabel: this.defaultSelectionLabel,
        });
      } else {
        logger.info('No filtered targeting data to report.');
      }
    }
  }

  private addListeners(): void {
    this.adCommunicator.on('TaggedAdServed', (event) => {
      this.taggedAdServedEventHandler(event);
    });
    this.adCommunicator.on('AdInteraction', (event) => {
      this.placementAdInteractionHandler(event);
    });
  }

  private validatePlacements(): void {
    this.placementMap.forEach((placement) => {
      if (!placement.hasValidSlot()) {
        placement.determineSlot();
      }
    });
  }

  // @ts-ignore
  protected placementAdInteractionHandler(event: {
    data: AdCommunicatorEvents['AdInteraction'];
  }): void {}

  private taggedAdServedEventHandler(event: {
    data: AdCommunicatorEvents['TaggedAdServed'];
  }): void {
    logger.info(`TaggedAdServed event received by Page for adUnitPath: ${event.data.adUnitPath}`);

    const placement = this.locateContainingPlacement(event);

    if (placement) {
      const currentOpportunity = placement.currentOpportunity;
      if (!currentOpportunity) {
        // If no Prebid auction took place, but a Nodals ad in Prebid mode was served
        // by the 1st party ad server, we cannot log an impression
        logger.warn(`No current opportunity found for placement: ${placement.adUnitCode}`);
        return;
      }
      logger.debug(`TaggedAdServed event matched to placement for adUnit: ${placement.adUnitCode}`);
      placement.handleTaggedAdDelivery(
        event.data.deliveryId as string,
        event.data.impressionId as string,
      );

      // Send opportunity data back to the ad
      this.adCommunicator.sendMessage(
        event.data.window,
        NodalsMessageEvent.OPPORTUNITY_DATA,
        {
          id: currentOpportunity.id,
          gad: placement.adUnitCode,
        },
        this.config,
      );
    } else {
      logger.warn(
        `No matching placement found for impression for adUnitPath: ${event.data.adUnitPath}`,
      );
    }
  }
}

export { Page, PageSession };
